<script setup>
import { computed, ref } from "vue";

// eslint-disable-next-line no-undef
const emit = defineEmits(["update:modelValue", "confirm", "cancel", "close"]);

// eslint-disable-next-line no-undef
const props = defineProps({
  modelValue: { type: Boolean, default: false },
  title: { type: String, default: "Eliminar elemento" },
  message: { type: String, default: "¿Esta seguro que desea continuar?" },
  secondaryMessage: { type: String, default: "Esta accion no se puede deshacer." },
  confirmText: { type: String, default: "Eliminar" },
  cancelText: { type: String, default: "Cancelar" },
  loadingText: { type: String, default: "Eliminando..." },
  loading: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  width: { type: [String, Number], default: 500 },
  titleIcon: { type: [String, Array], default: "trash" },
  showTitleIcon: { type: Boolean, default: true },
  confirmAction: { type: Function, default: null },
  cancelAction: { type: Function, default: null },
});

const internalLoading = ref(false);

const isBusy = computed(() => props.loading || internalLoading.value);

const closeDialog = () => {
  emit("update:modelValue", false);
  emit("close");
};

const updateModelValue = (value) => {
  if (!value) {
    handleCancel();
    return;
  }

  emit("update:modelValue", value);
};

const handleCancel = async () => {
  if (isBusy.value) return;

  if (props.cancelAction) {
    await props.cancelAction();
  }

  emit("cancel");
  closeDialog();
};

const handleConfirm = async () => {
  if (isBusy.value || props.disabled) return;

  emit("confirm");

  if (!props.confirmAction) return;

  internalLoading.value = true;
  try {
    const shouldClose = await props.confirmAction();
    if (shouldClose !== false) {
      closeDialog();
    }
  } finally {
    internalLoading.value = false;
  }
};
</script>

<template>
  <el-dialog :model-value="modelValue" :width="width" :show-close="false" :lock-scroll="true"
    class="shared-delete-confirm-dialog" style="border-radius: 20px;" @close="handleCancel"
    @update:model-value="updateModelValue">
    <div class="shared-delete-confirm-dialog__content">
      <div class="shared-delete-confirm-dialog__header">
        <div v-if="title" class="title-text shared-delete-confirm-dialog__title">
          <font-awesome-icon v-if="showTitleIcon && titleIcon" :icon="titleIcon"
            class="shared-delete-confirm-dialog__title-icon" />
          {{ title }}
        </div>
        <img class="shared-delete-confirm-dialog__close pointer justify-content-end"
          src="@/assets/images/aspa_blue_small_entrada.png" alt="" @click="handleCancel">
      </div>
      <div class="row shared-delete-confirm-dialog__body">
        <div class="col-md-12 el-col-xs-24">
          <p v-if="message" class="shared-delete-confirm-dialog__message">
            {{ message }}
          </p>
          <p v-if="secondaryMessage" class="shared-delete-confirm-dialog__secondary-message">
            {{ secondaryMessage }}
          </p>
        </div>
        <div class="row mt-4">
          <div class="col d-flex justify-content-end gap-3 shared-delete-confirm-dialog__actions"
            :class="{ 'shared-delete-confirm-dialog__actions--extra': $slots['extra-action'] }">
            <slot name="extra-action" :busy="isBusy" />
            <button type="button" class="btn btn_yellow px-4 shared-delete-confirm-dialog__button" :disabled="isBusy"
              @click="handleCancel">
              {{ cancelText }}
            </button>
            <button type="button" class="btn btn_red px-4 shared-delete-confirm-dialog__button"
              :disabled="isBusy || disabled" @click="handleConfirm">
              {{ isBusy ? loadingText : confirmText }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<style lang="scss" scoped>
.shared-delete-confirm-dialog__content {
  padding-bottom: 8px;
}

.shared-delete-confirm-dialog__header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 12px;
  margin-top: 2px;
}

.shared-delete-confirm-dialog__title {
  display: flex;
  align-items: center;
  gap: 12px;
  color: $font-blue-color;
}

.shared-delete-confirm-dialog__title-icon {
  width: 30px;
  min-width: 30px;
  font-size: 30px;
}

.shared-delete-confirm-dialog__close {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.shared-delete-confirm-dialog__body {
  margin: 0;
}

.shared-delete-confirm-dialog__message {
  margin-bottom: 6px;
  color: #1f2937;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.35;
  white-space: pre-wrap;
}

.shared-delete-confirm-dialog__secondary-message {
  margin-bottom: 0;
  color: #6b7280;
  white-space: pre-wrap;
}

.shared-delete-confirm-dialog__actions {
  width: 100%;
}

.shared-delete-confirm-dialog__button {
  width: 40%;
}
.shared-delete-confirm-dialog__actions--extra {
  flex-wrap: wrap;
  :deep(.btn) { flex: 1 1 140px; width: auto; min-height: 44px; margin: 0; }
}

@media (max-width: 767px) {
  .shared-delete-confirm-dialog__content {
    padding: 0 2px 8px;
  }

  .shared-delete-confirm-dialog__header {
    align-items: flex-start;
  }

  .shared-delete-confirm-dialog__title {
    font-size: 26px;
    line-height: 1.15;
  }

  .shared-delete-confirm-dialog__actions {
    flex-direction: column;
  }

  .shared-delete-confirm-dialog__actions--extra :deep(.btn) { flex: 0 0 auto; width: 100%; }
  .shared-delete-confirm-dialog__button {
    width: 100%;
  }
}
</style>
